/** Actual source transferred from standalone-search f83fe2d1 via Registrar V2. Native lifecycle adaptation is documented in TRANSFER.json. */
/**
 * StackedSheet — the "Sheet #2" contract for panels that slide over a primary
 * record sheet rather than dismissing to nothing.
 *
 * The pattern predates this component by six months. DiscussionPanel introduced
 * it (`c30f064da`, "stacked Sheet"), three more panels copied it, and five specs
 * and plans name it. But it was transmitted by commit message and copy-paste,
 * with no code owner and no enforcement — which is how ResearchAidsPanel's
 * `aria-hidden` fix reached exactly one of the four copies, and how the header
 * ended up with two spellings of the same flex row.
 *
 * This component is the enforcement the pattern never had. It owns, so callers
 * cannot re-decide it:
 *   - full-bleed width beside the sidebar, `hideDefaultTitle`, no X close
 *   - a back arrow — never an X, because these stack over a parent sheet
 *   - `aria-hidden` on every decorative icon in the header
 *
 * See `.claude/docs/adr/0010-workbench-sheet-shell.md` (D1).
 */

import type { ComponentProps, ComponentType, ReactNode, SVGProps } from "react";
import { ArrowLeft, X } from "lucide-react";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
} from "@/components/ui/sheet";
import { RecordSheetContent } from "@/components/ui/record-sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The icon *component*, not a rendered element. Taking an element would let
 * each caller re-choose the size and re-forget `aria-hidden` — the two drifts
 * this shell exists to close (ADR 0010 C4, C5).
 */
type StackedSheetIcon = ComponentType<Omit<SVGProps<SVGSVGElement>, "ref">>;

export interface StackedSheetProps {
  open: boolean;
  /**
   * Named for the affordance rather than the mechanism. These sheets have no
   * trigger, so Radix only ever reports `false` — Escape, scrim click and the
   * back button are three routes to one outcome, and the shell funnels all of
   * them here.
   */
  onBack: () => void;
  icon?: StackedSheetIcon;
  titleId?: string;
  returnLabel?: string;
  showReturnLabel?: boolean;
  backButton?: boolean;
  headerContext?: ReactNode;
  headerAfter?: ReactNode;
  headerProps?: ComponentProps<"div"> & { "data-bento-page"?: string; "data-journey"?: string; "data-page-refusal"?: string };
  sectionProps?: ComponentProps<"section"> & { "data-visit"?: string; "data-visit-refusal"?: string };
  contentProps?: Omit<ComponentProps<typeof RecordSheetContent>, "children">;
  title: string;
  /** Count badge or phase pill. Renders inside the title, part of its name. */
  titleAdornment?: ReactNode;
  /** Status badges or actions. Renders beside the title, outside its name. */
  trailing?: ReactNode;
  children: ReactNode;
  /** Body layout only — the shell keeps the padding. */
  bodyClassName?: string;
  /** Footer contents. The shell supplies the footer chrome. */
  footer?: ReactNode;
}

export function StackedSheet({
  open,
  onBack,
  icon: Icon,
  titleId,
  returnLabel = "Back",
  showReturnLabel = false,
  backButton = true,
  headerContext,
  headerAfter,
  headerProps,
  sectionProps,
  contentProps,
  title,
  titleAdornment,
  trailing,
  children,
  bodyClassName,
  footer,
}: StackedSheetProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onBack();
        }
      }}
    >
      <RecordSheetContent hideDefaultTitle showCloseButton={false} {...contentProps} aria-labelledby={titleId ?? contentProps?.["aria-labelledby"]}>
        <section {...sectionProps} className={cn("flex min-h-0 flex-1 flex-col", sectionProps?.className)}>
        {/* Native zoom reduces height as well as width. A long title must
            leave room to read the record; the full header remains scrollable. */}
        <SheetHeader {...headerProps} className={cn("max-h-[40dvh] shrink-0 overflow-y-auto border-b", headerProps?.className)}>
          {headerContext}
          {/* `flex-wrap` is the superset of the two spellings this replaced: it
              never triggers for a header with no trailing content. */}
          <div className="flex flex-wrap items-center gap-3">
            <span data-return={!backButton ? "" : undefined}>
            <Button
              variant="ghost"
              size={showReturnLabel ? "default" : "icon"}
              className="min-h-[var(--control-height-compact)] shrink-0"
              data-act="exit"
              aria-label={returnLabel}
              onClick={onBack}
            >
              {backButton || showReturnLabel ? <ArrowLeft className="h-4 w-4" aria-hidden="true" /> : <X className="h-4 w-4" aria-hidden="true" />}
              <span className={showReturnLabel ? undefined : "sr-only"}>{returnLabel}</span>
            </Button>
            </span>
            <SheetTitle id={titleId} tabIndex={-1} className="flex min-w-0 items-center gap-2 text-title outline-none focus-visible:outline-2 focus-visible:outline-ring">
              {Icon && <Icon className="h-5 w-5" aria-hidden="true" />}
              {title}
              {titleAdornment}
            </SheetTitle>
            {trailing}
          </div>
          {headerAfter}
        </SheetHeader>

        <SheetBody className={cn("pt-4", bodyClassName)}>{children}</SheetBody>

        {footer ? (
          <SheetFooter className="max-h-[40dvh] shrink-0 overflow-y-auto border-t bg-card p-0">
            {footer}
          </SheetFooter>
        ) : null}
        </section>
      </RecordSheetContent>
    </Sheet>
  );
}
